import { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import FeaturedTable from '../../../../../../../components/FeaturedTable/FeaturedTable'
import ItemsPopup from '../../../../../../../components/Popups/ItemsPopup'
import Icon from '../../../../../../../components/Icons'
import { selectUserData } from '../../../../../../../reducers/userDataReducer'
import {
  getManageInspectionAllTemplates,
  updateChecklistTemplateStatus,
  removeChecklistTemplate,
  selectChecklistsDefaultTemplates,
  setSelectedChecklistTemplate,
} from '../../../../../../../reducers/Organisation/checklistsReducer'
import { selectActiveBuilding } from '../../../../../../../reducers/buildingSelectorReducer'
import { manageInspectionColumns } from '../../../../helpers'
import API from '../../../../../../../api/api'
import { getIntegrationLabel } from '../../helpers'

import MergeUnitTypesForm from './MergeUnitTypesForm'

const ChecklistsManageInspection = ({
  userData,
  defaultTemplates,
  customTemplates,
  getManageInspectionAllTemplates,
  updateChecklistTemplateStatus,
  removeChecklistTemplate,
  setSelectedChecklistTemplate,
}) => {
  const { t } = useTranslation(['checklists-settings'])
  const timeout = useRef(null)
  const [isSiteLoading, setSiteLoading] = useState(true)
  const [currentChecklistData, setCurrentChecklistData] = useState(null)
  const [unitTypes, setUnitTypes] = useState([])
  const [unitTypesWindowData, setUnitTypesWindowData] = useState(null)

  const { role } = userData.user

  const selectedBuilding = useSelector(selectActiveBuilding)

  const isIntegratedRealpage =
    selectedBuilding && selectedBuilding.realPageManagementId && selectedBuilding.realPagePropertyId
  const inIntegratedEntrata =
    selectedBuilding && selectedBuilding.entrataManagementId && selectedBuilding.entrataPropertyId
  const isIntegratedYardi =
    selectedBuilding && selectedBuilding.yardiManagementId && selectedBuilding.yardiPropertyId
  const isIntegratedRentManager =
    selectedBuilding &&
    selectedBuilding.rentManagerManagementId &&
    selectedBuilding.rentManagerPropertyId

  const isIntegratedWith =
    isIntegratedRealpage || inIntegratedEntrata || isIntegratedYardi || isIntegratedRentManager
  const integrationLabel = getIntegrationLabel(selectedBuilding)

  useEffect(() => {
    if (selectedBuilding) {
      setSiteLoading(true)
      try {
        timeout.current = setTimeout(() => {
          getManageInspectionAllTemplates(selectedBuilding.id).then(() => setSiteLoading(false))
        }, 1000)
      } catch (e) {
        throw new Error(e)
      }
    }
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [selectedBuilding])

  const checkIntegrationBeforeChange = (checklistData, tempCategory) => {
    if (isIntegratedWith && !checklistData.active) {
      if (selectedBuilding) {
        setSiteLoading(true)
        try {
          API.getUnitTypes(selectedBuilding.id, checklistData.id).then(({ data }) => {
            setUnitTypes(data)
          })
        } catch ({ message }) {
          throw new Error(message)
        } finally {
          setSiteLoading(false)
          setCurrentChecklistData(checklistData)
        }
      }
    } else {
      changeActiveStatus(checklistData, tempCategory)
    }
  }

  const handleEditUnitTypes = (checklistObject) => {
    if (selectedBuilding) {
      setSiteLoading(true)
      try {
        API.getUnitTypes(selectedBuilding.id, checklistObject.id).then(({ data }) => {
          setUnitTypes(data)
        })
      } catch ({ message }) {
        throw new Error(message)
      } finally {
        setSiteLoading(false)
        setCurrentChecklistData(checklistObject)
      }
    }
  }

  const changeActiveStatus = (checklistData, tempCategory) => {
    setSiteLoading(true)
    const data = {
      active: !checklistData.active,
      buildingId: selectedBuilding.id,
      activeUnitTypes: checklistData.activeUnitTypes,
    }
    try {
      updateChecklistTemplateStatus(checklistData.id, data, tempCategory)
    } catch ({ message }) {
      throw new Error(message)
    } finally {
      setSiteLoading(false)
    }
  }

  const handleSubmit = (activeUnitTypes) => {
    setSiteLoading(true)
    const data = {
      active: true,
      buildingId: selectedBuilding.id,
      activeUnitTypes,
    }
    try {
      updateChecklistTemplateStatus(
        currentChecklistData.id,
        data,
        currentChecklistData.isTemplate ? 'templates' : 'custom',
      )
    } catch ({ message }) {
      throw new Error(message)
    } finally {
      setSiteLoading(false)
      setCurrentChecklistData(null)
    }
  }

  const getActionByClickOnCell = ({ row, column }) => {
    if (column.id === 'Unit type' && row.original.activeUnitTypes) {
      setUnitTypesWindowData(row.original)
    }
  }

  return (
    <div className={`ctp-checklists-manage-inspection ${isSiteLoading ? 'is-loading' : ''}`}>
      <div className="header-title-container">
        <div className="title">{t('checklists-settings:manageInspection.templatesTitle')}</div>
        <div className="action-fields">
          {isIntegratedWith ? (
            <div className="integration-information">
              <Icon icon="exclamationPoint" />
              {t('checklists-settings:manageInspection.integratedWith', {
                label: integrationLabel,
              })}
            </div>
          ) : (
            <div className="integration-information without-integration">
              <Icon icon="exclamationPoint" />
              {t('checklists-settings:manageInspection.notIntegrated')}
            </div>
          )}
        </div>
      </div>
      {defaultTemplates && defaultTemplates.length ? (
        <div className="templates-table">
          <FeaturedTable
            onCellClick={getActionByClickOnCell}
            columns={manageInspectionColumns(
              isIntegratedWith,
              removeChecklistTemplate,
              (checklistData) => checkIntegrationBeforeChange(checklistData, 'templates'),
              setSelectedChecklistTemplate,
              role,
              t,
            )}
            data={defaultTemplates}
          />
        </div>
      ) : (
        <div className="no-data">{t('checklists-settings:manageInspection.noDataMessage')}</div>
      )}
      <div className="header-title-container">
        <div className="title">{t('checklists-settings:manageInspection.customTitle')}</div>
      </div>
      {customTemplates && customTemplates.length ? (
        <div className="templates-table">
          <FeaturedTable
            onCellClick={getActionByClickOnCell}
            columns={manageInspectionColumns(
              isIntegratedWith,
              removeChecklistTemplate,
              (checklistData) => checkIntegrationBeforeChange(checklistData, 'custom'),
              setSelectedChecklistTemplate,
              undefined,
              t,
            )}
            data={customTemplates}
          />
        </div>
      ) : (
        <div className="no-data">{t('checklists-settings:manageInspection.noDataMessage')}</div>
      )}
      {currentChecklistData && (
        <MergeUnitTypesForm
          handleSubmit={handleSubmit}
          handleCancel={() => {
            setCurrentChecklistData(null)
            setUnitTypes([])
          }}
          checklistData={currentChecklistData}
          unitTypes={unitTypes}
          isSiteLoading={isSiteLoading}
          integrationLabel={integrationLabel}
        />
      )}
      {unitTypesWindowData && (
        <ItemsPopup
          title={t('checklists-settings:manageInspection.unitTypePopupTitle')}
          data={unitTypesWindowData}
          handleCancel={() => setUnitTypesWindowData(null)}
          openMergeUnitTypesForm={(checklistObject) => handleEditUnitTypes(checklistObject)}
        />
      )}
    </div>
  )
}

ChecklistsManageInspection.propTypes = {
  userData: PropTypes.object,
  defaultTemplates: PropTypes.array,
  customTemplates: PropTypes.array,
  getManageInspectionAllTemplates: PropTypes.func,
  updateChecklistTemplateStatus: PropTypes.func,
  removeChecklistTemplate: PropTypes.func,
  setSelectedChecklistTemplate: PropTypes.func,
  buildings: PropTypes.array,
}

export default connect(
  (state) => {
    const allTemplates = selectChecklistsDefaultTemplates(state)
    return {
      userData: selectUserData(state),
      defaultTemplates: allTemplates ? allTemplates.templates : [],
      customTemplates: allTemplates ? allTemplates.custom : [],
    }
  },
  {
    getManageInspectionAllTemplates,
    updateChecklistTemplateStatus,
    removeChecklistTemplate,
    setSelectedChecklistTemplate,
  },
)(ChecklistsManageInspection)
